/**
 * Mega Menu Component JavaScript - Full Width Panel Version
 * Complete functionality for desktop/mobile navigation, search, and cart integration
 */

class MegaMenu {
  constructor() {
    this.menu = document.querySelector('[data-mega-menu]');
    if (!this.menu) return;

    // Elements
    this.panelTriggers = this.menu.querySelectorAll('[data-panel-trigger]');
    this.panels = this.menu.querySelectorAll('[data-panel]');
    this.panelsContainer = this.menu.querySelector('[data-panels-container]');
    this.mobileToggle = this.menu.querySelector('[data-mobile-menu-toggle]');
    this.mobileDrawer = this.menu.querySelector('[data-mobile-drawer]');
    this.mobileCloseBtn = this.mobileDrawer?.querySelector('[data-drawer-close]');
    this.mobileBackBtn = this.mobileDrawer?.querySelector('.mega-menu__mobile-drawer-back');
    this.searchTriggers = this.menu.querySelectorAll('[data-search-trigger], [data-mobile-search-trigger]');
    this.searchPanel = this.menu.querySelector('[data-search-panel]');

    // State
    this.activePanel = null;
    this.isOpen = false;
    this.searchCache = {};
    this.cartUpdateInProgress = false;

    // Infinite scroll state
    this.searchPagination = {
      currentQuery: '',
      currentPage: 1,
      pageSize: 12,
      totalResults: 0,
      totalPages: 0,
      loadedResults: 0,
      maxResults: 50,
      isLoading: false,
      hasMore: true
    };

    // Initialize
    this.init();
  }

  init() {
    this.setupPanels();
    this.setupMobileMenu();
    this.setupSearch();
    this.setupCartDrawer();
    this.setupAccessibility();
    this.setupStickyBehavior();
    this.updateCartCount();
    this.listenToCartUpdates();
  }

  // Panel Setup for Desktop
  setupPanels() {
    if (!this.panelTriggers.length) return;

    // Create overlay element for panels
    this.createOverlay();

    this.panelTriggers.forEach(trigger => {
      const panelId = trigger.getAttribute('data-panel-trigger');
      const panel = this.menu.querySelector(`[data-panel="${panelId}"]`);

      if (!panel) return;

      // Hover to open panel
      trigger.addEventListener('mouseenter', () => {
        // Close search panel if open when hovering over menu items
        if (this.isSearchOpen()) {
          this.closeSearch();
        }
        this.openPanel(panel, trigger);
      });

      // Click to toggle panel (keep for mobile/accessibility)
      // On desktop, don't prevent navigation - let the link work normally
      trigger.addEventListener('click', (e) => {
        // Only prevent default on mobile or if it's a button (not a link)
        if (window.innerWidth <= 768 || trigger.tagName === 'BUTTON') {
          e.preventDefault();
          e.stopPropagation();

          if (this.activePanel === panel) {
            this.closePanel();
          } else {
            this.openPanel(panel, trigger);
          }
        }
        // On desktop with anchor tags, allow natural navigation
      });
    });

    // Close panel and search on mouse leave from menu area
    this.menu.addEventListener('mouseleave', () => {
      if (this.activePanel) {
        this.closePanel();
      }
      // Note: We don't auto-close search on mouse leave to allow users to interact with results
    });

    // Close panel when clicking outside
    document.addEventListener('click', (e) => {
      if (this.activePanel && !this.menu.contains(e.target)) {
        this.closePanel();
      }
    });

    // Close panel on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.activePanel) {
        this.closePanel();
      }
    });
  }

  createOverlay() {
    this.overlay = document.createElement('div');
    this.overlay.className = 'mega-menu__overlay';
    document.body.appendChild(this.overlay);

    this.overlay.addEventListener('click', () => {
      this.closePanel();
    });
  }

  openPanel(panel, trigger) {
    // Close any open panel first
    if (this.activePanel && this.activePanel !== panel) {
      this.closePanel(false);
    }

    // Close search panel if open
    if (this.isSearchOpen()) {
      this.closeSearch();
    }

    // Open new panel
    panel.setAttribute('aria-hidden', 'false');
    trigger.setAttribute('aria-expanded', 'true');
    this.overlay.classList.add('mega-menu__overlay--active');

    this.activePanel = panel;
    this.activeTrigger = trigger;

    // Trap focus in panel
    this.trapFocus(panel);
  }

  closePanel(resetFocus = true) {
    if (!this.activePanel) return;

    this.activePanel.setAttribute('aria-hidden', 'true');
    if (this.activeTrigger) {
      this.activeTrigger.setAttribute('aria-expanded', 'false');
      if (resetFocus) {
        this.activeTrigger.focus();
      }
    }

    this.overlay.classList.remove('mega-menu__overlay--active');
    this.releaseFocus();

    this.activePanel = null;
    this.activeTrigger = null;
  }

  // Mobile Menu Setup
  setupMobileMenu() {
    if (!this.mobileToggle || !this.mobileDrawer) return;

    // Toggle button
    this.mobileToggle.addEventListener('click', () => this.toggleMobileMenu());

    // Overlay close
    const overlay = this.mobileDrawer.querySelector('[data-drawer-overlay]');
    if (overlay) {
      overlay.addEventListener('click', () => this.closeMobileMenu());
    }

    // Close button
    if (this.mobileCloseBtn) {
      this.mobileCloseBtn.addEventListener('click', () => this.closeMobileMenu());
    }

    // Header back button is handled in setupMobileLevels

    // Setup multi-level navigation
    this.setupMobileLevels();

    // Setup expandable sections
    this.setupMobileExpandables();
  }

  setupMobileLevels() {
    if (this.mobileLevelsSetup) return; // Prevent duplicate setup

    // State
    this.activeMobilePanel = null;
    this.mobilePanelStack = [];

    // Panel triggers
    const panelTriggers = this.mobileDrawer.querySelectorAll('[data-mobile-panel-trigger]');
    const backButtons = this.mobileDrawer.querySelectorAll('[data-menu-back]');

    panelTriggers.forEach(trigger => {
      const panelId = trigger.getAttribute('data-mobile-panel-trigger');
      const panel = this.mobileDrawer.querySelector(`[data-mobile-panel="${panelId}"]`);

      if (!panel) {
        console.warn(`Mobile panel not found for trigger: ${panelId}`);
        return;
      }

      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.openMobilePanel(panel);
      });
    });

    backButtons.forEach(backBtn => {
      backBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.closeMobilePanel();
      });
    });

    this.mobileLevelsSetup = true;
  }

  setupMobileExpandables() {
    const expandableToggles = this.mobileDrawer.querySelectorAll('[data-mobile-expandable-toggle]');

    expandableToggles.forEach(toggle => {
      const content = toggle.parentElement.querySelector('[data-expandable-content]');
      const icon = toggle.querySelector('.mega-menu__mobile-expand-icon');

      if (!content) return;

      toggle.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        const isExpanded = content.classList.contains('expanded');

        if (isExpanded) {
          // Collapse
          content.classList.remove('expanded');
          if (icon) {
            icon.style.transform = 'rotate(270deg)';
          }
        } else {
          // Expand
          content.classList.add('expanded');
          if (icon) {
            icon.style.transform = 'rotate(0deg)';
          }
        }
      });
    });
  }

  openMobilePanel(panel) {
    // Slide current panel to the left (if any)
    if (this.activeMobilePanel) {
      this.mobilePanelStack.push(this.activeMobilePanel);
      this.activeMobilePanel.classList.remove('mega-menu__mobile-panel--active');
      this.activeMobilePanel.classList.add('mega-menu__mobile-panel--sliding-out');
    }

    // Slide new panel in from the right
    panel.classList.remove('mega-menu__mobile-panel--sliding-out');
    panel.classList.add('mega-menu__mobile-panel--active');
    this.activeMobilePanel = panel;

    // Update header to show back button
    this.updateMobileHeader();
  }

  closeMobilePanel() {
    if (!this.activeMobilePanel) return;

    // Store reference to the current panel
    const currentPanel = this.activeMobilePanel;

    // Show previous panel from the stack (if any)
    if (this.mobilePanelStack.length > 0) {
      // Get the previous panel
      const previousPanel = this.mobilePanelStack.pop();

      // Slide current panel out to the right
      currentPanel.classList.remove('mega-menu__mobile-panel--active');

      // Show the previous panel immediately
      previousPanel.classList.remove('mega-menu__mobile-panel--sliding-out');
      previousPanel.classList.add('mega-menu__mobile-panel--active');

      // Update the active panel reference
      this.activeMobilePanel = previousPanel;
    } else {
      // No previous panel, just close the current one
      currentPanel.classList.remove('mega-menu__mobile-panel--active');
      this.activeMobilePanel = null;
    }

    // Clean up sliding-out class after animation
    setTimeout(() => {
      currentPanel.classList.remove('mega-menu__mobile-panel--sliding-out');
    }, 300);

    // Update header to show appropriate button
    this.updateMobileHeader();
  }

  updateMobileHeader() {
    if (!this.mobileCloseBtn || !this.mobileBackBtn) return;

    const headerContainer = this.mobileDrawer?.querySelector('.mega-menu__mobile-drawer-header-container');

    if (this.activeMobilePanel) {
      // In submenu - show back button, hide close button container
      if (headerContainer) {
        headerContainer.style.display = 'none';
      }
      this.mobileBackBtn.style.display = 'flex';
    } else {
      // In main menu - show close button container, hide back button
      if (headerContainer) {
        headerContainer.style.display = 'flex';
      }
      this.mobileBackBtn.style.display = 'none';
    }
  }

  toggleMobileMenu() {
    if (this.isOpen) {
      this.closeMobileMenu();
    } else {
      this.openMobileMenu();
    }
  }

  openMobileMenu() {
    this.mobileDrawer.classList.add('mega-menu__mobile-drawer--open');
    this.mobileToggle.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
    this.isOpen = true;
    this.trapFocus(this.mobileDrawer);

    // Ensure header shows close button initially
    this.updateMobileHeader();
  }

  closeMobileMenu() {
    this.mobileDrawer.classList.remove('mega-menu__mobile-drawer--open');
    this.mobileToggle.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    this.isOpen = false;
    this.releaseFocus();

    // Reset mobile panels
    if (this.activeMobilePanel) {
      this.activeMobilePanel.classList.remove('mega-menu__mobile-panel--active');
      this.activeMobilePanel = null;
    }
    this.mobilePanelStack = [];
  }


  // Cart Drawer Setup
  setupCartDrawer() {
    // Find all cart trigger buttons
    const cartTriggers = this.menu.querySelectorAll('[data-action="open-drawer"][data-drawer-id="mini-cart"]');

    if (!cartTriggers.length) return;

    cartTriggers.forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.openCartDrawer();
      });
    });
  }

  openCartDrawer() {
    const cartDrawer = document.querySelector('#mini-cart');
    if (cartDrawer) {
      cartDrawer.open = true;
    }
  }

  // Search Setup
  setupSearch() {
    if (!this.searchTriggers.length || !this.searchPanel) return;

    this.searchInput = this.searchPanel.querySelector('[data-search-input]');
    this.searchResults = this.searchPanel.querySelector('[data-search-results]');
    this.searchSuggestions = this.searchPanel.querySelector('[data-search-suggestions]');
    this.searchResultsContainer = this.searchPanel.querySelector('.mega-menu__search-results-container');

    this.searchLayout = this.searchResults?.dataset.searchLayout || 'combined';
    if (this.searchLayout === 'tabbed') {
      this.tabsNav = this.searchResults.querySelector('#mm-search-tabs-nav');
      this.productsPanel = this.searchResults.querySelector('#mm-search-products');
      this.collectionsPanel = this.searchResults.querySelector('#mm-search-collections');
      this.activeTab = 'products';
      if (this.tabsNav) {
        this.tabsNav.addEventListener('tabs-nav:changed', (e) => {
          const button = e.detail && e.detail.button;
          this.activeTab = (button && button.dataset.tab) || 'products';
        });
      }
    }

    this.searchTriggers.forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        // Toggle search panel - close if already open, open if closed
        if (this.isSearchOpen()) {
          this.closeSearch();
        } else {
          this.openSearch();
        }
      });
    });

    // Close buttons
    this.searchPanel.querySelectorAll('[data-search-close]').forEach(btn => {
      btn.addEventListener('click', () => this.closeSearch());
    });

    // Close search when clicking outside
    document.addEventListener('click', (e) => {
      if (this.isSearchOpen() &&
          !this.searchPanel.contains(e.target) &&
          !Array.from(this.searchTriggers).some(trigger => trigger.contains(e.target))) {
        this.closeSearch();
      }
    });

    // Search input
    if (this.searchInput) {
      this.searchInput.addEventListener('input', this.debounce(() => {
        this.performSearch(this.searchInput.value);
      }, 300));
    }

    // Setup infinite scroll
    this.setupInfiniteScroll();
  }

  isSearchOpen() {
    return this.searchPanel && this.searchPanel.getAttribute('aria-hidden') === 'false';
  }

  openSearch() {
    // Close any open mega menu panels
    if (this.activePanel) {
      this.closePanel();
    }

    this.searchPanel.setAttribute('aria-hidden', 'false');

    // Prevent body scroll
    document.body.classList.add('mega-menu-search-open');
    document.body.style.overflow = 'hidden';

    setTimeout(() => {
      if (this.searchInput) {
        this.searchInput.focus();
      }
    }, 100);

    // Show suggestions initially if no search term
    if (this.searchInput && !this.searchInput.value.trim() && this.searchSuggestions) {
      this.searchSuggestions.style.display = 'block';
    }

  }

  closeSearch() {
    this.searchPanel.setAttribute('aria-hidden', 'true');

    // Restore body scroll
    document.body.classList.remove('mega-menu-search-open');
    document.body.style.overflow = '';

    if (this.searchInput) {
      this.searchInput.value = '';
    }
    this._clearSearchResults();
    if (this.searchSuggestions) {
      this.searchSuggestions.style.display = 'block';
    }
  }

  _clearSearchResults() {
    if (!this.searchResults) return;
    if (this.searchLayout === 'tabbed') {
      if (this.productsPanel) this.productsPanel.innerHTML = '';
      if (this.collectionsPanel) this.collectionsPanel.innerHTML = '';
      this._updateTabCounts(0, 0);
      if (this.tabsNav) this.tabsNav.hidden = true;
    } else {
      this.searchResults.innerHTML = '';
    }
  }

  async performSearch(query) {
    if (!query || query.length < 2) {
      this._clearSearchResults();
      if (this.searchSuggestions) {
        this.searchSuggestions.style.display = 'block';
      }
      this.resetPagination();
      return;
    }

    if (this.searchSuggestions) {
      this.searchSuggestions.style.display = 'none';
    }

    if (this.searchPagination.currentQuery !== query) {
      this.resetPagination();
      this.searchPagination.currentQuery = query;
    }

    if (this.searchLayout === 'tabbed') {
      return this._performTabbedSearch(query);
    }

    const cacheKey = `${query}_page1`;
    if (this.searchCache[cacheKey]) {
      const cached = this.searchCache[cacheKey];
      this.displaySearchResults(cached.products, false);
      this.appendCollectionResults(cached.collections || '');
      return;
    }

    this.searchResults.innerHTML = '<div class="mega-menu__search-loading">Searching...</div>';

    try {
      const [productsResponse, collectionsResponse] = await Promise.all([
        fetch(`/search?section_id=mega-menu-search-results&q=${encodeURIComponent(query)}&type=product&page=1`),
        fetch(`/search/suggest?section_id=mega-menu-collection-search&q=${encodeURIComponent(query)}&resources[type]=collection&resources[limit]=6&resources[limit_scope]=each`)
      ]);
      const [productsHtml, collectionsHtml] = await Promise.all([
        productsResponse.text(),
        collectionsResponse.text()
      ]);

      this.searchCache[cacheKey] = { products: productsHtml, collections: collectionsHtml };
      this.displaySearchResults(productsHtml, false);
      this.appendCollectionResults(collectionsHtml);
    } catch (error) {
      console.error('Search error:', error);
      this.searchResults.innerHTML = '<div class="mega-menu__search-error">Error loading results</div>';
    }
  }

  async _performTabbedSearch(query) {
    this._showTabs();

    const cacheKey = `${query}_tabbed`;
    if (this.searchCache[cacheKey]) {
      const cached = this.searchCache[cacheKey];
      this._renderTabbedResults(cached.products, cached.collections);
      return;
    }

    const loading = '<div class="mega-menu__search-loading">Searching...</div>';
    if (this.productsPanel) this.productsPanel.innerHTML = loading;
    if (this.collectionsPanel) this.collectionsPanel.innerHTML = loading;

    try {
      const [productsResponse, collectionsResponse] = await Promise.all([
        fetch(`/search?section_id=mega-menu-search-results&q=${encodeURIComponent(query)}&type=product&page=1`),
        fetch(`/search/suggest?section_id=mega-menu-collection-search&q=${encodeURIComponent(query)}&resources[type]=collection&resources[limit]=10&resources[limit_scope]=each`)
      ]);
      const [productsHtml, collectionsHtml] = await Promise.all([
        productsResponse.text(),
        collectionsResponse.text()
      ]);

      this.searchCache[cacheKey] = { products: productsHtml, collections: collectionsHtml };
      this._renderTabbedResults(productsHtml, collectionsHtml);
    } catch (error) {
      console.error('Search error:', error);
      const errorHtml = '<div class="mega-menu__search-error">Error loading results</div>';
      if (this.productsPanel) this.productsPanel.innerHTML = errorHtml;
      if (this.collectionsPanel) this.collectionsPanel.innerHTML = '';
    }
  }

  _renderTabbedResults(productsHtml, collectionsHtml) {
    let productsCount = 0;
    let collectionsCount = 0;

    if (this.productsPanel) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = productsHtml;
      const sectionContent = tempDiv.querySelector('.shopify-section--mega-menu-search-results');
      const resultsGrid = sectionContent?.querySelector('.mega-menu__search-results-grid');

      if (resultsGrid) {
        productsCount = parseInt(resultsGrid.dataset.totalResults, 10) || 0;
        this.searchPagination.totalResults = productsCount;
        this.searchPagination.pageSize = parseInt(resultsGrid.dataset.pageSize, 10) || 12;
        this.searchPagination.totalPages = parseInt(resultsGrid.dataset.totalPages, 10) || 0;
        this.searchPagination.currentPage = parseInt(resultsGrid.dataset.currentPage, 10) || 1;
        this.searchPagination.loadedResults = resultsGrid.querySelectorAll('.mega-menu__search-result-item').length;
        this.searchPagination.hasMore =
          this.searchPagination.currentPage < this.searchPagination.totalPages &&
          this.searchPagination.loadedResults < this.searchPagination.maxResults;
        this.searchPagination.isLoading = false;
        this.productsPanel.innerHTML = sectionContent.innerHTML;
      } else {
        this.productsPanel.innerHTML = '<div class="mega-menu__search-empty">No results found</div>';
        this.searchPagination.hasMore = false;
      }
    }

    if (this.collectionsPanel) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = collectionsHtml;
      const grid = tempDiv.querySelector('.mega-menu__search-collections-grid');

      if (grid) {
        grid.classList.add('mega-menu__search-collections-grid--tabbed');
        collectionsCount = grid.querySelectorAll('.collection-search-card').length;
        this.collectionsPanel.innerHTML = '';
        this.collectionsPanel.appendChild(grid);
      } else {
        this.collectionsPanel.innerHTML = '<div class="mega-menu__search-empty">No results found</div>';
      }
    }

    this._updateTabCounts(productsCount, collectionsCount);
    this._smartSwitchTab(productsCount, collectionsCount);
  }

  _showTabs() {
    if (!this.tabsNav || !this.tabsNav.hidden) return;
    this.tabsNav.hidden = false;
    if (typeof this.tabsNav._adjustNavigationPosition === 'function') {
      requestAnimationFrame(() => this.tabsNav._adjustNavigationPosition(false));
    }
  }

  _updateTabCounts(productsCount, collectionsCount) {
    if (!this.tabsNav) return;
    const writeCount = (tab, count) => {
      const span = this.tabsNav.querySelector(`[data-tab="${tab}"] [data-count]`);
      if (span) span.textContent = count > 0 ? ` (${count})` : '';
    };
    writeCount('products', productsCount);
    writeCount('collections', collectionsCount);
    if (typeof this.tabsNav._adjustNavigationPosition === 'function') {
      requestAnimationFrame(() => this.tabsNav._adjustNavigationPosition());
    }
  }

  _smartSwitchTab(productsCount, collectionsCount) {
    if (!this.tabsNav || typeof this.tabsNav.selectButton !== 'function') return;
    const currentCount = this.activeTab === 'products' ? productsCount : collectionsCount;
    const otherCount = this.activeTab === 'products' ? collectionsCount : productsCount;
    if (currentCount === 0 && otherCount > 0) {
      const otherTab = this.activeTab === 'products' ? 'collections' : 'products';
      const otherButton = this.tabsNav.querySelector(`[data-tab="${otherTab}"]`);
      if (otherButton) {
        this.tabsNav.selectButton(otherButton, false);
      }
    }
  }

  appendCollectionResults(html) {
    const existing = this.searchResults.querySelector('.mega-menu__search-collections');
    if (existing) {
      existing.remove();
    }

    if (!html) return;

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    const collectionsSection = tempDiv.querySelector('.mega-menu__search-collections');
    if (!collectionsSection) return;

    const emptyMessage = this.searchResults.querySelector('.mega-menu__search-empty');
    if (emptyMessage) {
      emptyMessage.remove();
    }

    this.searchResults.insertBefore(collectionsSection, this.searchResults.firstChild);
  }

  displaySearchResults(html, append = false) {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    const sectionContent = tempDiv.querySelector('.shopify-section--mega-menu-search-results');
    const resultsGrid = sectionContent?.querySelector('.mega-menu__search-results-grid');

    if (resultsGrid) {
      const totalResults = parseInt(resultsGrid.dataset.totalResults) || 0;
      const pageSize = parseInt(resultsGrid.dataset.pageSize) || 12;
      const totalPages = parseInt(resultsGrid.dataset.totalPages) || 0;
      const currentPage = parseInt(resultsGrid.dataset.currentPage) || 1;

      this.searchPagination.totalResults = totalResults;
      this.searchPagination.pageSize = pageSize;
      this.searchPagination.totalPages = totalPages;
      this.searchPagination.currentPage = currentPage;

      if (append) {
        const existingGrid = this.searchResults.querySelector('.mega-menu__search-results-grid');

        if (existingGrid) {
          this.removeLoadingSpinner();

          const newItems = resultsGrid.querySelectorAll('.mega-menu__search-result-item');
          newItems.forEach(item => existingGrid.appendChild(item.cloneNode(true)));

          this.searchPagination.loadedResults += newItems.length;

          this.searchPagination.hasMore =
            this.searchPagination.currentPage < this.searchPagination.totalPages &&
            this.searchPagination.loadedResults < this.searchPagination.maxResults;
        }
      } else {
        this.searchResults.innerHTML = sectionContent.innerHTML;

        const items = resultsGrid.querySelectorAll('.mega-menu__search-result-item');
        this.searchPagination.loadedResults = items.length;

        this.searchPagination.hasMore =
          this.searchPagination.currentPage < this.searchPagination.totalPages &&
          this.searchPagination.loadedResults < this.searchPagination.maxResults;
      }

      this.searchPagination.isLoading = false;
    } else {
      if (!append) {
        this.searchResults.innerHTML = '<div class="mega-menu__search-empty">No results found</div>';
        this.searchPagination.hasMore = false;
      }
      this.searchPagination.isLoading = false;
    }
  }

  setupInfiniteScroll() {
    if (!this.searchResultsContainer) return;

    this.searchResultsContainer.addEventListener('scroll', () => {
      const scrollTop = this.searchResultsContainer.scrollTop;
      const scrollHeight = this.searchResultsContainer.scrollHeight;
      const clientHeight = this.searchResultsContainer.clientHeight;

      if (scrollTop + clientHeight >= scrollHeight - 200) {
        this.loadMoreResults();
      }
    });
  }

  async loadMoreResults() {
    if (this.searchPagination.isLoading || !this.searchPagination.hasMore) {
      return;
    }

    if (this.searchLayout === 'tabbed' && this.activeTab !== 'products') {
      return;
    }

    if (this.searchPagination.loadedResults >= this.searchPagination.maxResults) {
      this.searchPagination.hasMore = false;
      return;
    }

    this.searchPagination.isLoading = true;
    this.showLoadingSpinner();

    const query = this.searchPagination.currentQuery;
    const nextPage = this.searchPagination.currentPage + 1;
    const cacheKey = `${query}_page${nextPage}`;

    try {
      let html;
      if (this.searchCache[cacheKey]) {
        html = this.searchCache[cacheKey];
      } else {
        const response = await fetch(
          `/search?section_id=mega-menu-search-results&q=${encodeURIComponent(query)}&type=product&page=${nextPage}`
        );
        html = await response.text();
        this.searchCache[cacheKey] = html;
      }

      this.displaySearchResults(html, true);
    } catch (error) {
      console.error('Error loading more results:', error);
      this.searchPagination.isLoading = false;
      this.removeLoadingSpinner();
    }
  }

  resetPagination() {
    this.searchPagination = {
      currentQuery: '',
      currentPage: 1,
      pageSize: 12,
      totalResults: 0,
      totalPages: 0,
      loadedResults: 0,
      maxResults: 50,
      isLoading: false,
      hasMore: true
    };
  }

  showLoadingSpinner() {
    // Remove existing spinner if present
    this.removeLoadingSpinner();

    // Create spinner element
    const spinner = document.createElement('div');
    spinner.className = 'mega-menu__search-loading-spinner';
    spinner.innerHTML = '<div class="mega-menu__spinner"></div>';

    // Append to results container
    const resultsGrid = this.searchResults.querySelector('.mega-menu__search-results-grid');
    if (resultsGrid) {
      this.searchResults.appendChild(spinner);
    }
  }

  removeLoadingSpinner() {
    const spinner = this.searchResults.querySelector('.mega-menu__search-loading-spinner');
    if (spinner) {
      spinner.remove();
    }
  }

  // Listen to cart updates
  listenToCartUpdates() {
    // Listen for cart updates from other components
    document.addEventListener('cart:updated', (e) => {
      if (e.detail && e.detail.cart) {
        this.updateCartCount(e.detail.cart.item_count);
      }
    });

    // Also listen for the standard Shopify cart update event
    document.addEventListener('cart:change', (e) => {
      if (e.detail && e.detail.cart) {
        this.updateCartCount(e.detail.cart.item_count);
      }
    });
  }

  updateCartCount(count) {
    if (count === undefined) {
      // Get initial count from DOM
      const countElement = document.querySelector('[data-cart-count]');
      count = countElement ? parseInt(countElement.textContent) : 0;
    }

    document.querySelectorAll('[data-cart-count]').forEach(element => {
      element.textContent = count;
    });
  }

  // Sticky Behavior
  setupStickyBehavior() {
    // Find the section wrapper
    const sectionWrapper = this.menu.closest('.section-mega-menu');
    if (!sectionWrapper) return;

    let scrollTimeout;

    const handleScroll = () => {
      // Clear the timeout
      clearTimeout(scrollTimeout);

      // Add scrolled class immediately when scrolled
      if (window.scrollY > 10) {
        sectionWrapper.classList.add('section-mega-menu--scrolled');
      } else {
        sectionWrapper.classList.remove('section-mega-menu--scrolled');
      }
    };

    // Throttle scroll events for better performance
    window.addEventListener('scroll', () => {
      if (!scrollTimeout) {
        scrollTimeout = setTimeout(() => {
          handleScroll();
          scrollTimeout = null;
        }, 10);
      }
    }, { passive: true });

    // Check initial scroll position
    handleScroll();
  }

  // Accessibility
  setupAccessibility() {
    // Handle focus trap
    this.focusableElements = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    this.firstFocusableElement = null;
    this.lastFocusableElement = null;
  }

  trapFocus(container) {
    const focusableElements = container.querySelectorAll(this.focusableElements);
    this.firstFocusableElement = focusableElements[0];
    this.lastFocusableElement = focusableElements[focusableElements.length - 1];

    container.addEventListener('keydown', this.handleFocusTrap.bind(this));
  }

  handleFocusTrap(e) {
    if (e.key !== 'Tab') return;

    if (e.shiftKey) {
      if (document.activeElement === this.firstFocusableElement) {
        e.preventDefault();
        this.lastFocusableElement.focus();
      }
    } else {
      if (document.activeElement === this.lastFocusableElement) {
        e.preventDefault();
        this.firstFocusableElement.focus();
      }
    }
  }

  releaseFocus() {
    if (this.firstFocusableElement) {
      this.firstFocusableElement = null;
      this.lastFocusableElement = null;
    }
  }

  // Utility Functions
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new MegaMenu());
} else {
  new MegaMenu();
}